import React, { useState, useRef } from 'react'
import { ListStyles } from '../styles'
import { Document, useDocumentShareLink } from '../../../../services/documents.service'
import { formatFileSize } from '../../../../utils/utils'
import { format, parseISO, isValid } from 'date-fns'
import { Field } from '../../../../components/Input/CheckboxInput/styles'
import { FaExclamation, FaFileLines, FaFolder, FaSpinner } from 'react-icons/fa6'
import { FaSearch } from 'react-icons/fa'
import { ButtonRound } from '../../../../components/Header/styles'
import { Item } from './styles'
import ModalViewer, { ModalHandle } from '../../../../components/Modals/ModalDocumentViewer'; // Importando o modal
import OverlayLoading from '../../../../components/OverlayLoading'; // Importando o overlay de loading
import { useUserStore } from '../../../../store/user'
import { usePermissions } from '../../../../hooks/usePermissions'

export interface ListItemHandle {
  checkValue: (value: boolean) => void
}

export interface ListItemProps {
  file: Document
  onClickItem: (file: Document, e: any) => void
  onDoubleClickItem: (file: Document, e: any) => void
  isEven?: boolean
}

const ListItem: React.ForwardRefRenderFunction<ListItemHandle, ListItemProps> = (
  { file, onClickItem, onDoubleClickItem, isEven = true },
  ref,
) => {
  const [checked, setChecked] = React.useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const modalRef = useRef<ModalHandle>(null)
  const overlayRef = useRef<any>(null)
  const { user } = useUserStore()
  const { canEditDocuments } = usePermissions()

  // Verificar permissões baseadas em grupo ao invés de role do usuário
  const canEdit = canEditDocuments


  /**
   * Obtém a data preferencial para exibição
   * Prioriza: data dos metadata > data de criação
   */
  const getDisplayDate = (document: Document): Date | null => {
    try {
      // Verificar se existe documentTypeWithData e metadata
      if (document.documentTypeWithData?.metadata && Array.isArray(document.documentTypeWithData.metadata)) {
        // Procurar por campos de data nos metadata
        const dateFields = ['data', 'date', 'data_criacao', 'data_criado', 'created_at', 'data_documento'];
        
        for (const meta of document.documentTypeWithData.metadata) {
          if (meta.name && meta.value && dateFields.some(field => 
            meta.name.toLowerCase().includes(field.toLowerCase())
          )) {
            try {
              // Tentar diferentes formatos de data
              const dateFormats = [
                'dd/MM/yyyy',
                'dd/MM/yyyy HH:mm',
                'yyyy-MM-dd',
                'yyyy-MM-dd HH:mm:ss',
                'dd-MM-yyyy',
                'MM/dd/yyyy'
              ];
              
              for (const dateFormat of dateFormats) {
                try {
                  const parsedDate = parseISO(meta.value);
                  if (isValid(parsedDate)) {
                    return parsedDate;
                  }
                } catch {
                  // Tentar parse com format específico
                  try {
                    const parsedDate = parseISO(meta.value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
                    if (isValid(parsedDate)) {
                      return parsedDate;
                    }
                  } catch {
                    continue;
                  }
                }
              }
            } catch (error) {
              console.warn('Erro ao processar data dos metadata:', error);
              continue;
            }
          }
        }
      }
      
      // Fallback para data de criação - verificar se é válida
      if (document.createdAt) {
        try {
          const createdAt = new Date(document.createdAt);
          if (isValid(createdAt)) {
            return createdAt;
          }
        } catch (error) {
          console.warn('Erro ao processar createdAt:', error);
        }
      }
      
      // Se tudo falhar, retornar null
      return null;
    } catch (error) {
      console.warn('Erro geral em getDisplayDate:', error);
      return null;
    }
  }

  // Hook para obter link de compartilhamento quando um documento é selecionado
  const { data: shareLinkData, isLoading: isLoadingShareLink, error: shareLinkError } = useDocumentShareLink(selectedDocumentId)

  React.useImperativeHandle(
    ref,
    () => ({
      checkValue(value) {
        setChecked(value)
      },
      checked,
      _id: file._id,
      url: file.url,
      name: file.name,
    }),
    [checked, file],
  )

  // Função para abrir o modal com o documento
  const handleOpenDocument = () => {
    if (!file.isFolder && file._id) {
      // Mostrar overlay de loading
      overlayRef.current?.handleOpen()
      setSelectedDocumentId(file._id)
    }
  }

  // Função para abrir pasta
  const handleOpenFolder = () => {
    if (file.isFolder) {
      onDoubleClickItem(file, { stopPropagation: () => {} })
    }
  }

  // Efeito para abrir o modal quando o link de compartilhamento for obtido
  React.useEffect(() => {
    if (shareLinkData?.success && shareLinkData.shareLink) {
      // Fechar overlay de loading
      overlayRef.current?.handleClose()
      modalRef.current?.openModal(shareLinkData.shareLink, shareLinkData.document)
      setSelectedDocumentId(null) // Reset para evitar re-abertura
    }
  }, [shareLinkData])

  // Efeito para tratar erros
  React.useEffect(() => {
    if (shareLinkError) {
      console.error('Erro ao obter link de compartilhamento:', shareLinkError)
      // Fechar overlay de loading em caso de erro
      overlayRef.current?.handleClose()
      setSelectedDocumentId(null)
    }
  }, [shareLinkError])

  return (
    <>
      <ListStyles.Row
        key={file._id}
        onClick={file.isFolder ? handleOpenFolder : handleOpenDocument}
        onDoubleClick={file.isFolder ? (e) => onDoubleClickItem(file, e) : undefined}
        style={{ 
          cursor: 'pointer',
          opacity: isLoadingShareLink ? 0.7 : 1,
          backgroundColor: isEven ? '#ffffff' : '#e9ecef'
        }}
      >
        <ListStyles.NameFile minWidth>
          {canEdit && (
            <ButtonRound
              style={{
                width: 20,
                height: 20,
                minWidth: 20,
                minHeight: 20,
              }}
              onClick={(e) => {
                onClickItem(file, e)
              }}
            >
              <FaExclamation size={15} color={'white'} />
            </ButtonRound>
          )}
          <Item>
            <Field
              onClick={(e) => {
                e.stopPropagation()
              }}
              onChange={(e) => setChecked(e.target.checked)}
              checked={checked}
              type='checkbox'
            />
          </Item>

          <Item>
            {isLoadingShareLink && selectedDocumentId === file._id ? (
              <FaSpinner size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : file.isFolder ? (
              <FaFolder size={20} />
            ) : (
              <FaFileLines size={20} />
            )}
          </Item>

          <ListStyles.Name style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {file.name}
              {file.ocrStatus === 'completed' && (
                <span title="OCR Aplicado - Conteúdo pesquisável" style={{ marginLeft: 8, color: '#4caf50', fontSize: '0.8em' }}>
                  <FaSearch size={12} />
                </span>
              )}
            </div>
            {file.ocrSnippet && (
              <span style={{ fontSize: '0.75em', color: '#666', marginTop: 4, fontStyle: 'italic', maxWidth: '400px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.ocrSnippet}
              </span>
            )}
          </ListStyles.Name>
        </ListStyles.NameFile>
        <ListStyles.NameFile style={{ justifyContent: 'flex-end', width: '10%' }}>
          <ListStyles.Name>
            {(() => {
              try {
                // Verificação mais robusta da data
                let displayDate = null;
                
                // Tentar obter data dos metadata primeiro
                if (file.documentTypeWithData?.metadata && Array.isArray(file.documentTypeWithData.metadata)) {
                  const dateFields = ['data', 'date', 'data_criacao', 'data_criado', 'created_at', 'data_documento'];
                  
                  for (const meta of file.documentTypeWithData.metadata) {
                    if (meta.name && meta.value && dateFields.some(field => 
                      meta.name.toLowerCase().includes(field.toLowerCase())
                    )) {
                      try {
                        const parsedDate = new Date(meta.value);
                        if (isValid(parsedDate)) {
                          displayDate = parsedDate;
                          break;
                        }
                      } catch (e) {
                        continue;
                      }
                    }
                  }
                }
                
                // Se não encontrou nos metadata, tentar createdAt
                if (!displayDate && file.createdAt) {
                  try {
                    const createdAt = new Date(file.createdAt);
                    if (isValid(createdAt)) {
                      displayDate = createdAt;
                    }
                  } catch (e) {
                    // Ignorar erro
                  }
                }
                
                // Se encontrou uma data válida, formatar
                if (displayDate && isValid(displayDate)) {
                  return format(displayDate, 'dd/MM/yyyy');
                }
                
                return '-';
              } catch (error) {
                console.warn('Erro ao formatar data:', error);
                return '-';
              }
            })()}
          </ListStyles.Name>
        </ListStyles.NameFile>

        <ListStyles.NameFile style={{ justifyContent: 'flex-end', width: '30%' }}>
          <ListStyles.Name>
            {file.isFolder ? '-' : (() => {
              // Prioridade: documentTypeWithData.documentType.name > documentType.name > documentType (string) > '-'
              const result = file.documentTypeWithData?.documentType?.name || 
                            (typeof file.documentType === 'object' && file.documentType?.name ? file.documentType.name : null) ||
                            (typeof file.documentType === 'string' ? file.documentType : '-');
              
              return result;
            })()}
          </ListStyles.Name>
        </ListStyles.NameFile>

        <ListStyles.NameFile style={{ justifyContent: 'flex-end', width: '20%' }}>
          <ListStyles.Name>
            {file.isFolder ? '-' : (file.pages ? `${file.pages} ${file.pages === 1 ? 'página' : 'páginas'}` : '-')}
          </ListStyles.Name>
        </ListStyles.NameFile>

        <ListStyles.NameFile style={{ justifyContent: 'flex-end', width: '20%' }}>
          <ListStyles.Name>
            {' '}
            {file.isFolder ? '-' : formatFileSize(Number(file?.size || 0))}
          </ListStyles.Name>
        </ListStyles.NameFile>
      </ListStyles.Row>
      
      {/* Modal para visualização do documento */}
      <ModalViewer ref={modalRef} />
      
      {/* Overlay de loading */}
      <OverlayLoading ref={overlayRef}>
        Carregando documento...
      </OverlayLoading>
    </>
  )
}

export default React.forwardRef(ListItem)
